/**
 * THE RAILWAY — WHERE WORK IS, AND WHERE IT STOPPED.
 *
 * Anthony has twice described leads "dropping out of the railway", and the
 * import found seven jobs that had done exactly that: marked booked, with
 * no date on them, so they sat on no board and chased nobody.
 *
 * The old dashboard could not have shown that, because it counted what was
 * IN each stage and never what had fallen out between two of them. A
 * funnel drawn from status alone always looks healthy: every row is
 * somewhere.
 *
 * So each stage carries two numbers. `count` is what is sitting there, and
 * `stuck` is how many of those have been sitting longer than they should.
 * The second one is the one worth looking at — a stage with forty in it
 * and none stuck is a busy shop; a stage with three in it and all three
 * stuck is a hole.
 */

import { db } from "./client.ts";
import type { SupabaseClient } from "@supabase/supabase-js";

export type Stage = {
  key: string;
  label: string;
  count: number;
  /** Sitting here longer than this stage should take. */
  stuck: number;
  /** Where clicking it goes. */
  href: string;
  /** Said in words when something is wrong, because a number alone is not a reason. */
  note?: string;
};

const DAY = 86400000;
const since = (days: number) => new Date(Date.now() - days * DAY).toISOString();

/**
 * HOW FAR BACK THE RAILWAY IS COUNTING.
 *
 * "14 quotes" means nothing without it. Fourteen this week is a busy shop;
 * fourteen ever is a dead one. The range travels with the click too, so
 * the list you land on is the same set that produced the number — a count
 * you cannot get back to is a number you cannot trust.
 */
export type Span = "today" | "week" | "month" | "all";

/**
 * ALL TIME SAYS WHAT IT REALLY MEANS.
 *
 * Nine rows in ten came from the migration and carry its timestamp, so
 * "All time" is not a longer version of "This month" — it is a different
 * dataset with dates that are not true. Somebody who picks it should have
 * been told that by the control, not surprised by it afterwards.
 */
export const SPANS = [
  { key: "today" as Span, label: "Today" },
  { key: "week" as Span, label: "This week" },
  { key: "month" as Span, label: "This month" },
  { key: "all" as Span, label: "All time, incl. imported" },
];

/**
 * WHICH COLUMN DATES EACH STAGE — declared once, read by the railway AND
 * by the page a click lands on.
 *
 * It used to be declared twice, and the two copies disagreed about work in
 * progress: the railway counted it undated while the list filtered it by
 * creation date. So the tile said four and the page it opened showed two,
 * which makes every other number on the screen suspect.
 *
 * A stage is dated by the event that PUT work there, never by when the
 * paperwork was raised. Booked is the appointment. Done is the day it was
 * finished. Started is when somebody picked up a wrench.
 */
export const DATED_BY: Record<string, string> = {
  estimate: "created_at",
  scheduled: "scheduled_at",
  in_progress: "started_at",
  completed: "completed_at",
  // Billed and Paid are NOT dated from jobs at all any more — see below.
  invoiced: "created_at",
  paid: "created_at",
};

import { floorFor } from "../domain/watermark.ts";

/**
 * WHERE THE WINDOW ENDS, which only one stage has ever needed.
 *
 * Every other stage is dated by something that has already happened, so
 * "since Monday" is the whole question. Booked is dated by an appointment,
 * and appointments are in the future — so a "today" tile with no upper
 * bound counts next Tuesday's booking as today's, which it did. The Booked
 * number was the sum of everything on the calendar from midnight onwards.
 */
/**
 * ─── THE ANCHOR ────────────────────────────────────────────────────────
 *
 * A span used to be able to describe exactly one period: the one with
 * `new Date()` inside it. "Today" meant today and could not be made to
 * mean yesterday, which is why the dashboard's arrows could not move
 * through time and cycled the option list instead. See the note on the
 * stepper in src/ui/picker.ts.
 *
 * `anchor` is any day inside the period you want. It defaults to now, so
 * every existing caller keeps the behavior it had.
 */
export function spanTo(s: Span, anchor: Date = new Date()): string | null {
  const d = new Date(anchor);
  if (s === "today") { d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + 1); return d.toISOString(); }
  if (s === "week") { d.setDate(d.getDate() - d.getDay() + 7); d.setHours(0, 0, 0, 0); return d.toISOString(); }
  if (s === "month") return new Date(d.getFullYear(), d.getMonth() + 1, 1).toISOString();
  return null;
}

export function spanFrom(s: Span, anchor: Date = new Date()): string | null {
  const d = new Date(anchor);
  if (s === "today") { d.setHours(0, 0, 0, 0); return d.toISOString(); }
  if (s === "week") { d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0); return d.toISOString(); }
  if (s === "month") return new Date(d.getFullYear(), d.getMonth(), 1).toISOString();
  return null;
}

/**
 * ONE STEP, IN THE UNIT THE SPAN IS MADE OF.
 *
 * This is the whole point of the fix: what the arrows move by is decided
 * by what the middle of the control says. Day by day on Today, week by
 * week on This week, month by month on This month.
 *
 * `all` does not move. There is no period before all time, and an arrow
 * that silently does nothing is worse than one that is plainly off, so
 * `canStepSpan` exists for the control to ask before it draws itself.
 *
 * The month case sets the day to 1 BEFORE stepping. Stepping a month from
 * the 31st is the classic date bug: `setMonth` on 31 March lands on
 * 31 April, which does not exist, and JavaScript rolls it forward to
 * 1 May -- so going back one month from the 31st skips a month entirely.
 * From the 1st there is no such day to overflow.
 */
export const canStepSpan = (s: Span): boolean => s !== "all";

export function stepSpan(s: Span, anchor: Date, dir: number): Date {
  const d = new Date(anchor);
  if (s === "today") d.setDate(d.getDate() + dir);
  else if (s === "week") d.setDate(d.getDate() + 7 * dir);
  else if (s === "month") { d.setDate(1); d.setMonth(d.getMonth() + dir); }
  return d;
}

/**
 * WHAT THE MIDDLE OF THE CONTROL SAYS once the arrows have been used.
 *
 * "Today" has to stop saying Today when you are looking at Tuesday, or
 * the screen is lying about which day produced its numbers -- and this
 * screen's numbers are the ones a shop makes decisions on before nine in
 * the morning.
 *
 * Near days get their name rather than their date, because "Yesterday"
 * is what a person actually says and is read faster than a date they
 * then have to work out. Beyond that, the date.
 */
export function spanLabel(s: Span, anchor: Date = new Date()): string {
  const base = SPANS.find((x) => x.key === s)?.label ?? "";
  if (s === "all") return base;

  const midnight = (d: Date) => {
    const x = new Date(d); x.setHours(0, 0, 0, 0); return x;
  };
  const dayDiff = Math.round(
    (midnight(anchor).getTime() - midnight(new Date()).getTime()) / 864e5);

  if (s === "today") {
    if (dayDiff === 0) return "Today";
    if (dayDiff === -1) return "Yesterday";
    if (dayDiff === 1) return "Tomorrow";
    return anchor.toLocaleDateString("en-US",
      { weekday: "short", month: "short", day: "numeric" });
  }

  if (s === "week") {
    const from = new Date(spanFrom("week", anchor) ?? anchor);
    const thisWeek = new Date(spanFrom("week", new Date()) ?? anchor);
    if (from.getTime() === thisWeek.getTime()) return "This week";
    if (Math.round((from.getTime() - thisWeek.getTime()) / 864e5) === -7) return "Last week";
    return `Week of ${from.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  }

  const now = new Date();
  if (anchor.getFullYear() === now.getFullYear() && anchor.getMonth() === now.getMonth()) {
    return "This month";
  }
  return anchor.toLocaleDateString("en-US", {
    month: "long",
    // The year only when it is not this one. "September 2025" beside
    // "October" reads as a mistake; "September" alone beside it does not.
    ...(anchor.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/**
 * Invoices that were PAID in the window, counted once each.
 *
 * A split payment is two rows against one invoice and must not be two
 * jobs on the tile, so the distinct is done here rather than hoped for.
 * The volume in any period a person looks at is small; this is a handful
 * of rows, not a table scan.
 */
/**
 * Finished, and no invoice behind it. The one number on this screen that
 * is money the shop has earned and not asked for.
 *
 * Two reads and a set difference rather than a NOT EXISTS, because
 * PostgREST cannot express one and a wrong join here would either hide
 * the problem or invent it.
 */
async function finishedUnbilled(sb: SupabaseClient, from: string | null): Promise<number> {
  let jq = sb.from("jobs").select("id")
    .eq("is_training", false)
    // A warranty or goodwill job has no bill on purpose. See 0088.
    .is("no_charge_reason", null)
    .in("status", ["completed", "invoiced", "paid"]).limit(5000);
  if (from) jq = jq.gte("completed_at", from);
  const { data: jobs, error } = await jq;
  if (error) throw new Error(error.message);

  const ids = ((jobs ?? []) as Array<{ id: string }>).map((j) => j.id);
  if (!ids.length) return 0;

  const billed = new Set<string>();
  // Chunked: a list in a URL is a header, and undici refuses one past 16KB.
  for (let i = 0; i < ids.length; i += 120) {
    const { data } = await sb.from("invoices").select("job_id").in("job_id", ids.slice(i, i + 120));
    for (const r of (data ?? []) as Array<{ job_id: string | null }>) {
      if (r.job_id) billed.add(r.job_id);
    }
  }
  return ids.filter((id) => !billed.has(id)).length;
}

async function paidInPeriod(sb: SupabaseClient, from: string | null): Promise<number> {
  /**
   * THE RAILWAY COUNTS JOBS. Every other stage on it does, and this one
   * counted rows in the payments table -- so a year of work the shop did,
   * was paid for, and migrated without its receipts sat at "completed" and
   * never reached the end of the line. Over a thousand jobs.
   *
   * Anthony: "we did the work, we want the dashboard to say it. but we
   * don't have all the historical records."
   *
   * So it counts jobs that reached paid. The MONEY is untouched and still
   * comes from real payments -- Payroll, the reports and the takings all
   * read that table and none of them changed. A job counted here without a
   * receipt behind it says the work finished and was settled; it does not
   * add a penny to what the shop banked.
   *
   * REHEARSAL WORK IS STILL EXCLUDED. $618.25 of practice money was in the
   * takings on 3 September from invoices somebody settled while learning
   * the screens. A figure that includes invented money is a figure the
   * owner stops trusting.
   */
  let q = sb.from("jobs").select("id", { count: "exact", head: true })
    .eq("status", "paid").eq("is_training", false);
  if (from) q = q.gte("completed_at", from);
  const { count, error } = await q;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

export async function funnel(span: Span = "all", anchor: Date = new Date()): Promise<Stage[]> {
  const sb = await db();
  const from = spanFrom(span, anchor);

  /**
   * EACH STAGE IS DATED BY ITS OWN EVENT, not by when the job was created.
   *
   * This was wrong and Anthony caught it: two jobs booked for today showed
   * as zero, because "today" was filtering on created_at and those jobs
   * were raised last week. A railway set to today should answer "what is
   * happening today", and for Booked that is the APPOINTMENT date, not the
   * paperwork date.
   *
   * So every stage names the column that dates it. Booked goes by
   * scheduled_at, Done by completed_at, Leads and Quotes by when they came
   * in. In progress is deliberately not dated at all — work that is open is
   * open, and hiding it because it started on Tuesday would be the exact
   * kind of quiet disappearance this screen exists to stop.
   *
   * head:true means the rows never travel; only the count comes back.
   */
  const n = async (
    build: (q: any) => any,
    opts: { table?: string; dateOn?: string | null; until?: string | null } = {},
  ): Promise<number> => {
    let q = sb.from(opts.table ?? "jobs").select("id", { count: "exact", head: true });
    /**
     * PRACTICE WORK IS NOT ON THE RAILWAY.
     *
     * Nine training jobs were sitting across the live stages on 3
     * September, two of them on the day's schedule beside real bookings.
     * A railway that counts rehearsals is a railway nobody can act on,
     * and the whole purpose of this screen is that the numbers are worth
     * chasing. The job lists and the schedule still show them, because
     * somebody practicing has to be able to find what they just made.
     */
    if ((opts.table ?? "jobs") === "jobs") q = q.eq("is_training", false);

    /**
     * THE MIGRATION IS NOT ON THE RAILWAY EITHER.
     *
     * 1,774 jobs carry an import timestamp for a created_at, so any count
     * that reaches back past the watermark is counting a database load
     * rather than a shop. The floor applies to whatever column dates the
     * stage — Booked by its appointment, Done by its finish — because an
     * imported job with a real appointment date is still an imported job.
     *
     * `all` is the deliberate exception, and the control now says so.
     */
    const floor = floorFor(from, span === "all");
    if (floor && opts.dateOn) q = q.gte(opts.dateOn, floor);
    else if (floor && (opts.table ?? "jobs") === "jobs") q = q.gte("created_at", floor);
    else if (floor) q = q.gte("created_at", floor);
    // Only Booked passes one: see spanTo.
    if (opts.until && opts.dateOn) q = q.lt(opts.dateOn, opts.until);
    const { count, error } = await build(q);
    if (error) throw new Error(error.message);
    return count ?? 0;
  };

  const [
    leadsNew, leadsCold,
    quotes, quotesCold,
    booked, bookedNoDate,
    working, workingCold,
    done, doneCold,
    billed, billedCold,
    paid,
  ] = await Promise.all([
    // Leads and quotes are dated by when they arrived.
    n((q: any) => q.eq("kind", "lead").eq("status", "new"), { table: "enquiries", dateOn: "created_at" }),
    n((q: any) => q.eq("kind", "lead").eq("status", "new").lt("created_at", since(2)),
      { table: "enquiries", dateOn: "created_at" }),

    n((q: any) => q.eq("status", "estimate"), { dateOn: DATED_BY["estimate"]! }),
    n((q: any) => q.eq("status", "estimate").lt("created_at", since(7)),
      { dateOn: DATED_BY["estimate"]! }),

    /**
     * BOOKED IS DATED BY THE APPOINTMENT, and counts the appointments that
     * fell in the window — not the jobs still sitting at `scheduled`.
     *
     * This is the same correction Done got, for the same reason. A stage
     * is a count of what PASSED THROUGH it in the period, and counting
     * what is parked there instead means a booking disappears from the
     * tile the moment somebody starts the work. On a normal morning the
     * shop books four and the tile reads one, because the other three are
     * under way — which is precisely backwards, since those three are the
     * ones that went right.
     *
     * Cancelled is out, because a cancelled appointment did not happen.
     * So is `estimate`: a quote nobody has approved yet is not a booking
     * however many dates are pencilled on it.
     */
    n((q: any) => q.in("status", ["scheduled", "in_progress", "completed", "invoiced", "paid"]),
      { dateOn: DATED_BY["scheduled"]!, until: spanTo(span, anchor) }),
    // The exact shape the schema now forbids. Never date-filtered, because
    // a booking with no date has no date to filter on — which is the point.
    n((q: any) => q.eq("status", "scheduled").is("scheduled_at", null), { dateOn: null }),

    // Dated by when the work actually started, so this tile and the page
    // it opens are the same set. Anything open with no start time at all
    // would vanish from every span but All time, so it is counted as
    // fallout instead of quietly disappearing — which is the entire point
    // of this screen.
    n((q: any) => q.eq("status", "in_progress"), { dateOn: DATED_BY["in_progress"]! }),
    n((q: any) => q.eq("status", "in_progress").lt("started_at", since(3)),
      { dateOn: DATED_BY["in_progress"]! }),

    /**
     * DONE MEANS "GOT FINISHED", NOT "IS SITTING AT FINISHED".
     *
     * This counted `status = completed`, so a job finished at two and
     * invoiced at three left the Done tile the moment it was billed. On a
     * good day the shop finishes six and Done reads one, because the other
     * five moved on — which is exactly what Anthony saw: "jobs that are
     * paid and closed are not finished in the railway".
     *
     * A funnel stage is a count of what PASSED THROUGH it in the period.
     * Anything with a completed_at in the window was finished in the
     * window, whatever happened to it afterwards. Cancelled is excluded,
     * because a cancelled job did not get finished.
     */
    n((q: any) => q.in("status", ["completed", "invoiced", "paid"]),
      { dateOn: DATED_BY["completed"]! }),
    /**
     * THE LABEL SAID "finished but not billed" AND THE QUERY COUNTED
     * SOMETHING ELSE — jobs sitting at completed for more than two days.
     * A job finished this morning with no invoice is the one losing money,
     * and it was not counted until Thursday.
     *
     * Anthony: "some finished jobs were not put to paid in railway today."
     * They were finished. They were never billed, so they could never
     * reach paid, and nothing said so.
     */
    finishedUnbilled(sb, from),

    /**
     * BILLED AND PAID COME FROM INVOICES, not from a job's status.
     *
     * They used to be dated by `jobs.updated_at`, and that column is the
     * IMPORT TIMESTAMP for all 1,951 jobs — every one of them says
     * 2026-09-02. So Paid showed its full 1,066 whatever period you
     * picked, which is what Anthony saw as "paid is stuck on railway".
     * A column that holds the same value for every row is not a date.
     *
     * Invoices carry a real issued_at spanning a year, and this is the
     * same rule the reports already follow: money is counted from
     * invoices and payments, never from a job's status.
     */
    n((q: any) => q.eq("status", "open"), { table: "invoices", dateOn: "issued_at" }),
    n((q: any) => q.eq("status", "open").lt("issued_at", since(30)),
      { table: "invoices", dateOn: "issued_at" }),

    // Paid ends the railway. A railway with no end does not show the one
    // thing worth knowing: how much of what started actually finished.
    //
    // Dated by WHEN THE MONEY CAME IN, not when the bill went out. An
    // invoice raised on Monday and settled on Thursday is Thursday's
    // money; counting it on Monday put paid work on the wrong day and
    // left today's takings looking empty.
    paidInPeriod(sb, from),
  ]);

  return [
    { key: "leads", label: "Leads", count: leadsNew, stuck: leadsCold, href: `#/leads/${span}`,
      ...(leadsCold ? { note: `${leadsCold} unanswered for more than two days` } : {}) },
    { key: "quotes", label: "Quoted", count: quotes, stuck: quotesCold, href: `#/jobs/estimate/${span}`,
      ...(quotesCold ? { note: `${quotesCold} sent over a week ago with no answer` } : {}) },
    { key: "booked", label: "Booked", count: booked, stuck: bookedNoDate, href: `#/jobs/scheduled/${span}`,
      ...(bookedNoDate ? { note: `${bookedNoDate} booked with NO DATE — on no board` } : {}) },
    { key: "working", label: "In progress", count: working, stuck: workingCold, href: `#/jobs/in_progress/${span}`,
      ...(workingCold ? { note: `${workingCold} open more than three days` } : {}) },
    /**
     * DONE AND PAID WERE THE SAME TILE.
     *
     * Anthony: "Done is same as paid. Both in railway is redundant. Once
     * job is paid, job is also finished."
     *
     * The database agrees, and not approximately: of 1,667 jobs carrying a
     * finish date, 1,655 are paid, and NOT ONE paid job is missing its
     * finish date. Two tiles counting the same 99% of the work is two
     * places to read the same number and one more chance to read it wrong.
     *
     * So Done goes and Paid ends the railway. The twelve that really are
     * finished and not paid are not lost -- they become the stuck number
     * underneath Paid, which is where a stage's problem belongs. That is
     * the only thing Done was still telling anybody.
     */
    { key: "billed", label: "Billed", count: billed, stuck: billedCold, href: "#/invoices",
      ...(billedCold ? { note: `${billedCold} unpaid for over a month` } : {}) },
    { key: "paid", label: "Paid", count: paid, stuck: doneCold, href: "#/invoices",
      ...(doneCold ? { note: `${doneCold} finished with no money against them` } : {}) },
  ];
}

/**
 * WHAT LEFT THE RAILWAY WITHOUT REACHING THE END.
 *
 * This is the number the old dashboard could never show. A funnel built
 * from status always looks healthy, because every row is somewhere — the
 * ones that quietly stopped are still sitting in a stage looking like
 * work in progress, and the ones that were closed off vanish from the
 * picture entirely.
 *
 * So this counts the leavers on purpose: leads marked lost, jobs
 * cancelled, and the two silent ones — a lead that nobody ever answered
 * and nobody ever closed, and work that was finished and never billed.
 * Those last two are not decisions anybody made. They are the shop
 * forgetting, and they are money.
 */
export type Fallout = {
  key: string; label: string; count: number; href: string; money: boolean;
  /** The same thing counted over all time, for the gray figure beside it. */
  ever: number;
};

/**
 * FINISHED, WITH NOTHING WRITTEN DOWN ABOUT THE MONEY.
 *
 * NOT "never billed", and the difference matters. Anthony, on the 564:
 * "they were billed, they just paid cash and our old system sucked."
 *
 * So this counts jobs that were finished and carry no invoice — which on
 * the old system covers two completely different situations. Some are
 * money genuinely never asked for. Most are cash taken at the curb that
 * nobody ever wrote up. Calling the whole number "money left behind" would
 * have put a false claim in front of somebody, which is worse than showing
 * nothing at all.
 *
 * The count stays, because "there is no record of how this was settled" is
 * a real and useful thing to know. Only the claim about what it means is
 * removed. Going forward the answer is a payment row with method 'cash',
 * which the schema has and the old system did not.
 *
 * Counting `status = 'completed'` alone is also nearly right and wrong
 * enough to matter: eighteen of those jobs DO have an invoice, so the
 * status is stale rather than the money missing.
 *
 * PostgREST has no `not exists`, so the two id sets are fetched and
 * subtracted here. At this size that is a few hundred kilobytes and one
 * extra round trip; if it ever stops being cheap the answer is a view, not
 * a guess.
 */
async function countUnbilled(): Promise<number> {
  const sb = await db();
  const [done, billed] = await Promise.all([
    sb.from("jobs").select("id").eq("status", "completed")
      .is("no_charge_reason", null).limit(5000),
    sb.from("invoices").select("job_id").not("job_id", "is", null).limit(5000),
  ]);
  const hasInvoice = new Set((billed.data ?? []).map((r: { job_id: string }) => r.job_id));
  return ((done.data ?? []) as Array<{ id: string }>).filter((j) => !hasInvoice.has(j.id)).length;
}

/**
 * WHAT LEFT THE RAILWAY, IN THE WINDOW ABOVE IT.
 *
 * Anthony chose "windowed number, lifetime in small type", and the reason
 * this needed asking is that the row had NO date filter at all. Today,
 * this week, this month and all time showed the same figures, so the
 * number under a control that said Today included a lead from May. A row
 * that reads the same whatever you press is a row people stop pressing.
 *
 * Two counts now: what fell out inside the chosen window, which is the
 * one somebody can act on this morning, and the lifetime total in gray
 * beside it, which is the one that says whether it is getting better.
 *
 * `dateOn` names the column that dates each kind of leaving, the same way
 * the stages do. Not every one has a good column -- a lead abandoned is
 * dated by when it arrived, because nothing records the day somebody
 * stopped answering -- and where that is true it is said in the comment
 * rather than pretended away.
 */
export async function fallout(from: string | null = null): Promise<Fallout[]> {
  const sb = await db();
  const n = async (
    build: (q: any) => any, table: string, dateOn?: string,
  ): Promise<number> => {
    let q = sb.from(table).select("id", { count: "exact", head: true });
    if (from && dateOn) q = q.gte(dateOn, from);
    const { count } = await build(q);
    return count ?? 0;
  };

  /* Each of these twice: once inside the window, once over all time. The
     lifetime pass is the same query with `from` ignored, which is why the
     helper takes the column rather than baking the filter in. */
  const both = async (
    build: (q: any) => any, table: string, dateOn?: string,
  ): Promise<[number, number]> => {
    const win = await n(build, table, dateOn);
    if (!from || !dateOn) return [win, win];
    const all = await (async () => {
      const { count } = await build(
        sb.from(table).select("id", { count: "exact", head: true }));
      return count ?? 0;
    })();
    return [win, all];
  };

  const [lost, cancelled, abandoned, unbilled, unstarted] = await Promise.all([
    both((q: any) => q.eq("kind", "lead").eq("status", "lost"), "enquiries", "updated_at"),
    both((q: any) => q.eq("status", "cancelled"), "jobs", "updated_at"),
    // Never answered, never closed, and old enough that nobody is going to.
    // Dated by arrival: nothing records the day somebody stopped answering.
    both((q: any) => q.eq("kind", "lead").in("status", ["new", "contacted"])
      .lt("created_at", since(30)), "enquiries", "created_at"),
    (async () => { const u = await countUnbilled(); return [u, u] as [number, number]; })(),
    // Marked as being worked on, with no record of when that started.
    both((q: any) => q.eq("status", "in_progress").is("started_at", null), "jobs", "created_at"),
  ]);

  return [
    { key: "abandoned", label: "Leads nobody ever answered or closed",
      count: abandoned[0], ever: abandoned[1], href: "#/leads", money: true },
    // money:false — on the old data this is mostly cash nobody wrote up,
    // so flagging it as owed would be asserting something untrue.
    { key: "unbilled", label: "Finished, with no record of how it was paid",
      count: unbilled[0], ever: unbilled[1], href: "#/jobs", money: false },
    { key: "lost", label: "Leads closed as lost",
      count: lost[0], ever: lost[1], href: "#/leads", money: false },
    { key: "cancelled", label: "Jobs cancelled",
      count: cancelled[0], ever: cancelled[1], href: "#/jobs", money: false },
    ...(unstarted[1] ? [{ key: "unstarted",
      label: "Open, with no record of when work started",
      count: unstarted[0], ever: unstarted[1],
      href: "#/jobs/in_progress/all", money: false }] : []),
  ];
}
